import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { UpdateGroupWrapper } from "./styled";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseLegend } from "../../../components/form/legend/styled";
import { BaseOption } from "../../../components/form/option/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import Cookies from "universal-cookie";
import { UpdateGroupFormType } from "../../../types/container.type";
import { BaseButton } from "../../../components/button/styled";
import { retrieveAllSavingsProductsService } from "../../../utils/api/configuration/savingsproduct/retrieveAllSavingsProducts";
import { retrieveAllCustomersService } from "../../../utils/api/customerservice/customerlist/retrieveAllCustomers";
import { useEffect, useState } from "react";
import { retrieveSavingsAccountByCustomerIdService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByCustomerId";
import { retrieveGroupService } from "../../../utils/api/customerservice/grouplist/retrieveGroup";
import { retrieveAllMFIUsersService } from "../../../utils/api/usermanagement/mfiuser/retrieveAllMFIUsers";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";
import { retrieveBranchService } from "../../../utils/api/configuration/branch/retrieveBranch";

export const UpdateGroup: React.FC<UpdateGroupFormType> = ({
	formDetails,
	setFormDetails,
	error,
	isLoading,
	handleSubmit,
	groupId,
}) => {
	const accountTypes = ["savings account"];
	const statuses = ["active", "inactive"];
	const registrationTypes = ["formal", "informal"];
	const frequencies = ["daily", "weekly", "bi-weekly", "monthly", "quarterly"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const [groupDomiciledBranch, setGroupDomiciledBranch] = useState<Record<
		string,
		any
	> | null>(null);
	const [membersDetail, setMembersDetail] = useState<
		{ id: string; name: string; accountNumber: string }[]
	>([]);

	useEffect(() => {
		const fetchGroup = async () => {
			if (!groupId.trim()) return;
			try {
				const group = await retrieveGroupService(TOKEN, groupId);
				const groupDomiciledBranch = await retrieveBranchService(
					TOKEN,
					group.branch,
				);
				const members = await Promise.all(
					group?.members.map(async (memberId: string) => {
						const customer = await retrieveCustomerService(TOKEN, memberId);
						const savingsAccount =
							await retrieveSavingsAccountByCustomerIdService(TOKEN, memberId);
						return {
							id: memberId,
							name: `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`,
							accountNumber: savingsAccount?.accountNumber || "",
						};
					}),
				);
				setFormDetails(group);
				setMembersDetail(members);
				setGroupDomiciledBranch(groupDomiciledBranch);
			} catch (err) {
				console.error("Failed to fetch group information:", err);
			}
		};
		fetchGroup();
	}, [TOKEN, groupId, setFormDetails]);

	const { data: accountManagers } = useQuery({
		queryKey: ["accountManagers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllMFIUsersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: customers } = useQuery({
		queryKey: ["mfi-customers", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllCustomersService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const { data: savingsProducts } = useQuery({
		queryKey: ["savingsProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllSavingsProductsService(TOKEN);
			const user = await retrieveUserProfileService(TOKEN);
			return response?.data?.filter(
				(product: Record<string, any>) =>
					// product.serialNo === 1 &&
					product.status === "active" &&
					product.institution === user.institution,
			);
		},
		enabled: !!TOKEN,
	});

	const handleRetrieveGroupMember = async (memberId: string) => {
		try {
			const [memberName, memberAccountNumber] = await Promise.all([
				retrieveCustomerService(TOKEN, memberId),
				retrieveSavingsAccountByCustomerIdService(TOKEN, memberId),
			]);

			setMembersDetail((prev) => [
				...prev,
				{
					id: memberId,
					name: `${memberName?.firstName ?? ""} ${memberName?.lastName ?? ""}`,
					accountNumber: memberAccountNumber?.accountNumber || "",
				},
			]);
		} catch (error) {
			console.error("Error retrieving group member:", error);
		}
	};

	const handleRemoveGroupMember = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		member: { id: string; name: string; accountNumber: string },
	) => {
		e.preventDefault();
		setFormDetails((prev) => ({
			...prev,
			members: prev.members.filter((groupMember) => groupMember !== member.id),
		}));
		setMembersDetail((prev) =>
			prev.filter((detail) => detail.accountNumber !== member.accountNumber),
		);
	};

	const handleChange = async (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		if (name === "members") {
			if (
				!formDetails.members.includes(value as string) &&
				String(value).trim()
			) {
				setFormDetails((prev) => ({
					...prev,
					members: [...prev.members, value as string],
				}));
				await handleRetrieveGroupMember(value as string);
			}
		} else {
			setFormDetails((prev) => ({
				...prev,
				[name]: value,
			}));
		}
	};

	return (
		<UpdateGroupWrapper onSubmit={handleSubmit}>
			<BaseLegend fontweight={500} fontsize={"18"} color="var(--dark-color)">
				Enter required information to update group account.
			</BaseLegend>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Group Account Type{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							readOnly
							name="accountType"
							fontweight={400}
							value={formDetails.accountType}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Account Type</BaseOption>
							{accountTypes?.map((type: string, index: number) => {
								return (
									<BaseOption key={index} value={type}>
										{type
											?.split(" ")
											.map(
												(word: string) =>
													word.charAt(0).toUpperCase() + word.slice(1),
											)
											.join(" ")}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Branch{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							readOnly
							name="branch"
							value={groupDomiciledBranch?.name}
							placeholder="Assigned User Branch"
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Group Name{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="name"
							value={formDetails.name}
							placeholder="Enter Group Name"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Registration{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="registrationType"
							fontweight={400}
							value={formDetails.registrationType}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Registration Type</BaseOption>
							{registrationTypes?.map((type: string, index: number) => {
								return (
									<BaseOption key={index} value={type}>
										{type.charAt(0).toUpperCase() + type.slice(1)}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Savings Product{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							readOnly
							name="savingsProduct"
							fontweight={400}
							value={formDetails.savingsProduct}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Savings Product</BaseOption>
							{savingsProducts?.map(
								(savingsProduct: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={savingsProduct.id}>
											{savingsProduct.name}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Account Officer{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="accountManager"
							fontweight={400}
							value={formDetails.accountManager}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Account Manager</BaseOption>
							{accountManagers?.map(
								(accountManager: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={accountManager.id}>
											{`${accountManager?.firstName} ${accountManager?.lastName}`}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Meeting Day{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="meetingDay"
							type="date"
							value={formDetails.meetingDay}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Meeting Time{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="meetingTime"
							value={formDetails.meetingTime}
							placeholder="Enter Meeting Time"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Meeting Frequency{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="meetingFrequency"
							fontweight={400}
							value={formDetails.meetingFrequency}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Frequency</BaseOption>
							{frequencies?.map((frequency: string, index: number) => {
								return (
									<BaseOption key={index} value={frequency}>
										{frequency.charAt(0).toUpperCase() + frequency.slice(1)}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Meeting Location{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							type="text"
							name="meetingLocation"
							value={formDetails.meetingLocation}
							placeholder="Enter Meeting Location"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Email{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="email"
							type="email"
							value={formDetails.email}
							placeholder="Enter Email Address"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Primary Contact Person
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							required
							name="primaryContactName"
							fontweight={400}
							value={formDetails.primaryContactName}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Primary Contact Person</BaseOption>
							{customers?.map(
								(customer: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={customer.id}>
											{`${
												customer?.firstName.charAt(0).toUpperCase() +
												customer?.firstName.slice(1)
											} ${
												customer?.lastName.charAt(0).toUpperCase() +
												customer?.lastName.slice(1)
											}`}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Contact Phone
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							name="primaryContactPhone"
							value={formDetails.primaryContactPhone}
							placeholder="Enter Phone Number"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12, tablet: 6 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Status{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseSelect
							name="status"
							fontweight={400}
							value={formDetails.status}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Status</BaseOption>
							{statuses?.map((status: string, index: number) => {
								return (
									<BaseOption key={index} value={status}>
										{status.charAt(0).toUpperCase() +
											status.slice(1).toLowerCase()}
									</BaseOption>
								);
							})}
						</BaseSelect>
					</BaseFieldSet>
				</Grid2>
				<Grid2 size={{ mobile: 12 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">
							Mandate{" "}
							<Typography
								component={"span"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"var(--text-salmon-pink-color)"}
							>
								*
							</Typography>
						</BaseLabel>
						<BaseInput
							required
							multiline
							minRows={4}
							name="mandate"
							value={formDetails.mandate}
							placeholder="Description of Mandate (max 100 characters)"
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			<Box>
				<Typography
					variant="h3"
					fontFamily={"Poppins"}
					fontWeight={700}
					fontSize={20}
					lineHeight={"normal"}
					color="var(--dark-color)"
				>
					Add to Group
				</Typography>
			</Box>
			<Grid2
				container
				component={"div"}
				spacing={"var(--flex-gap)"}
				className="fieldset-grid-wrapper"
			>
				<Grid2 size={{ mobile: 12 }}>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)" sx={{ marginBlockStart: 0 }}>
							Select members to add to the group
						</BaseLabel>
						<BaseSelect
							required
							name="members"
							fontweight={400}
							value={" "}
							colour="var(--semi-gray-color)"
							onChange={(e) => handleChange(e)}
						>
							<BaseOption value={" "}>Select Member</BaseOption>
							{customers?.map(
								(customer: Record<string, any>, index: number) => {
									return (
										<BaseOption key={index} value={customer.id}>
											{`${
												customer?.firstName.charAt(0).toUpperCase() +
												customer?.firstName.slice(1)
											} ${
												customer?.lastName.charAt(0).toUpperCase() +
												customer?.lastName.slice(1)
											}`}
										</BaseOption>
									);
								},
							)}
						</BaseSelect>
						{membersDetail.length > 0 && (
							<Stack className="group-members-stack">
								{membersDetail?.map((member, index) => {
									return (
										<Stack
											key={index}
											gap={"var(--flex-gap)"}
											direction={{ laptop: "row" }}
											justifyContent={"space-between"}
											alignItems={{ laptop: "flex-end" }}
										>
											<Stack key={index} className="group-member-information">
												<BaseFieldSet>
													<BaseLabel colour="var(--dark-color)">
														Member Name
													</BaseLabel>
													<BaseInput
														required
														readOnly
														placeholder="Member Name"
														name={`member-${index}-name`}
														value={member.name}
													/>
												</BaseFieldSet>
												<BaseFieldSet>
													<BaseLabel colour="var(--dark-color)">
														Member Account Number
													</BaseLabel>
													<BaseInput
														required
														readOnly
														placeholder="Member Account Number"
														name={`member-${index}-account-number`}
														value={member.accountNumber}
													/>
												</BaseFieldSet>
											</Stack>
											<Box
												component={"div"}
												className="remove-member-button-box"
											>
												<BaseButton
													variant="text"
													padding="0"
													border="none"
													colour="var(--primary-color)"
													sx={{
														justifyContent: "right",
														width: { mobile: "100%", laptop: "auto" },
													}}
													onClick={(e) => handleRemoveGroupMember(e, member)}
												>
													<Typography
														variant={"button"}
														fontFamily={"inherit"}
														fontWeight={"inherit"}
														fontSize={"inherit"}
														lineHeight={"inherit"}
														color={"inherit"}
														textTransform={"inherit"}
													>
														Remove
													</Typography>
												</BaseButton>
											</Box>
										</Stack>
									);
								})}
							</Stack>
						)}
					</BaseFieldSet>
				</Grid2>
			</Grid2>
			{error && (
				<Typography
					fontFamily={"Inter"}
					fontWeight={"600"}
					fontSize={14}
					lineHeight={"normal"}
					color={"var(--text-salmon-pink-color)"}
					whiteSpace={"normal"}
				>
					{error}
				</Typography>
			)}
			<Box overflow={"hidden"}>
				<BaseButton
					type="submit"
					variant="contained"
					bgcolor="var(--primary-color)"
					disableElevation
					sx={{
						width: { mobile: "100%", miniTablet: "auto" },
					}}
				>
					{isLoading ? (
						<CircularProgress color="inherit" className="loader" />
					) : (
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Update Group
						</Typography>
					)}
				</BaseButton>
			</Box>
		</UpdateGroupWrapper>
	);
};
